import { useEffect, useMemo, useState } from "react";

import { capitalize } from "lodash";

import { useAvailableMetricsWithArgs } from "@features/sales-v3/hooks/use-available-metrics";
import { FETCH_CUSTOM_GOALS } from "@gql/creative-analytics";
import { useNorthbeamQuery } from "@hooks/http";
import { useFuzzySearch } from "@hooks/use-fuzzy-search";
import {
  type FetchCustomGoals,
  type SalesPageMetric,
} from "@north-beam/nb-client-side";

import { useMetricMap } from "../atoms";

import { useCurrentlyUpdatingMetric } from "./use-currently-updating-metric";

const FREQUENTLY_USED_METRICS_IDS = [
  "spend",
  "Attributed Rev",
  "Transactions",
  "visits",
  "ROAS",
  "CAC",
  "AOV",
  "cpm",
  "ctr",
  "ecpc",
];

const sortMetricsByFrequentlyUsed = (metrics: SalesPageMetric[]) =>
  metrics.sort((a, b) => {
    const aIndex = FREQUENTLY_USED_METRICS_IDS.indexOf(a.id);
    const bIndex = FREQUENTLY_USED_METRICS_IDS.indexOf(b.id);

    if (aIndex === -1 && bIndex === -1) {
      return a.name.localeCompare(b.name);
    }

    if (aIndex === -1) {
      return 1;
    }

    if (bIndex === -1) {
      return -1;
    }

    return aIndex - bIndex;
  });

const setMetricName = (metric: SalesPageMetric) => {
  let metricName = metric.name;
  if (metric.spec?.name) {
    metricName = metric.spec.name;
  } else if (metric.metadata?.name) {
    metricName = metric.metadata.name;
  }
  metric.name = metricName;
  return metric;
};

export const useFilteredMetrics = () => {
  const { accountingMode, setAccountingMode } = useCurrentlyUpdatingMetric();
  const [metricMap, setMetricMap] = useMetricMap();

  const [selectedCategory, setSelectedCategory] = useState("all");

  const { data: customGoalsData } =
    useNorthbeamQuery<FetchCustomGoals>(FETCH_CUSTOM_GOALS);

  const customGoals = useMemo(
    () => customGoalsData?.me.customGoals.goals ?? [],
    [customGoalsData?.me.customGoals],
  );

  const { metrics } = useAvailableMetricsWithArgs(
    {
      accountingMode,
      attributionModel: "northbeam_custom",
      attributionWindow: "1",
      timeGranularity: "daily",
    },
    { customGoals, hideBenchmarks: true },
  );

  const [filteredMetrics, { search, setSearch }] = useFuzzySearch(metrics, {
    searchKeys: ["name"],
  });

  const metricCategories = sortMetricsByFrequentlyUsed(filteredMetrics).reduce<
    Record<string, SalesPageMetric[]>
  >(
    (acc, metric) => {
      let type: string = metric.composition.type;
      if (type === "base") {
        type = "northbeam";
      }

      setMetricName(metric);

      if (!acc[type]) {
        acc[type] = [metric];
      } else {
        acc[type].push(metric);
      }
      return acc;
    },
    {
      northbeam: [],
      custom: [],
      facebook: [],
      google: [],
      pinterest: [],
      snapchat: [],
      tiktok: [],
    },
  );

  const categories = [
    {
      label: "All",
      value: "all",
    },
    ...Object.keys(metricCategories).map((category) => ({
      label: capitalize(category),
      value: category,
    })),
  ];

  useEffect(() => {
    setMetricMap((prevMetricMap) => ({
      ...metrics.reduce<typeof prevMetricMap>((acc, metric) => {
        acc[metric.id] = metric;
        return acc;
      }, prevMetricMap),
    }));
  }, [metrics, setMetricMap]);

  const getMetricById = (id: SalesPageMetric["id"]) => metricMap[id];

  return {
    metrics,
    getMetricById,
    accountingMode,
    setAccountingMode,
    selectedCategory,
    setSelectedCategory,
    search,
    setSearch,
    filteredMetrics,
    categories,
    metricCategories,
  };
};
